import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import type {
  ReturnsSettings as Settings,
  WriteRejection,
} from "@estelle/domain";
import { useId, useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import { LoadFailed } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RejectionNotice } from "@/modules/orders/screens/RejectionNotice";
import {
  useProjectDirectory,
  useUserDirectory,
} from "@/modules/tasks/directories";

/** Select values cannot be empty, so "no one" and "the default project" are this. */
const NONE = "none";

/**
 * Settings for returns (#529): who the follow-up task of a prepaid return goes
 * to, and which project return tasks are filed in.
 *
 * A prepaid parcel that comes back needs refunding in Shopify, which the ERP
 * never does itself, so receiving one creates the task "Refund order <number>".
 * Left on "no one", the task is made unassigned. Left on the default project,
 * tasks go in a project called Returns, made the first time one is needed.
 */
function Form({ saved }: { saved: Settings }) {
  const data = useDataSource();
  const queryClient = useQueryClient();
  const assigneeId = useId();
  const projectId = useId();
  const users = useUserDirectory();
  const projects = useProjectDirectory();

  const [assignee, setAssignee] = useState(saved.refundAssigneeUserId ?? NONE);
  const [project, setProject] = useState(saved.taskProjectId ?? NONE);
  const [rejection, setRejection] = useState<WriteRejection | null>(null);
  const [done, setDone] = useState(false);

  const save = useMutation({
    mutationFn: () =>
      data.returns.saveSettings({
        refundAssigneeUserId: assignee === NONE ? null : assignee,
        taskProjectId: project === NONE ? null : project,
      }),
    onSuccess: (result) => {
      if (result.ok) {
        setRejection(null);
        setDone(true);
        queryClient.setQueryData(["returns", "settings"], result.data);
      } else {
        setDone(false);
        setRejection(result.rejection);
      }
    },
  });

  const changed =
    assignee !== (saved.refundAssigneeUserId ?? NONE) ||
    project !== (saved.taskProjectId ?? NONE);

  return (
    <Card className="max-w-xl">
      <CardHeader>
        <CardTitle>Returns</CardTitle>
        <CardDescription>
          What happens after a returned parcel is received. A prepaid order that
          comes back needs refunding in Shopify; the ERP never moves money, so
          it makes a task for a person to do it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor={assigneeId} className="text-sm font-medium">
            Refund tasks go to
          </Label>
          <Select
            value={assignee}
            disabled={save.isPending}
            onValueChange={(value) => {
              setAssignee(value);
              setDone(false);
            }}
          >
            <SelectTrigger id={assigneeId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>No one (unassigned)</SelectItem>
              {(users.data?.items ?? []).map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {user.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={projectId} className="text-sm font-medium">
            Return tasks are filed in
          </Label>
          <Select
            value={project}
            disabled={save.isPending}
            onValueChange={(value) => {
              setProject(value);
              setDone(false);
            }}
          >
            <SelectTrigger id={projectId} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>
                Returns project (made when needed)
              </SelectItem>
              {(projects.data?.items ?? []).map((entry) => (
                <SelectItem key={entry.id} value={entry.id}>
                  {entry.key} · {entry.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {rejection ? (
          <RejectionNotice
            rejection={rejection}
            onReload={() => {
              setRejection(null);
            }}
          />
        ) : null}
        {done ? (
          <p
            role="status"
            className="text-sm text-emerald-700 dark:text-emerald-300"
          >
            Saved.
          </p>
        ) : null}

        <Button
          type="button"
          disabled={!changed || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? <Loader2 className="animate-spin" /> : null}
          Save
        </Button>
      </CardContent>
    </Card>
  );
}

export function ReturnsSettings() {
  const data = useDataSource();
  const query = useQuery({
    queryKey: ["returns", "settings"],
    queryFn: () => data.returns.getSettings(),
  });

  if (query.isPending) return <TableSkeleton rows={3} />;
  if (query.isError) {
    return (
      <LoadFailed
        message={query.error.message}
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }
  return <Form saved={query.data} />;
}
